/**
 * RingtonePlayerContext — global playback state for ringtones.
 *
 * Tracks the currently playing ringtone, its audio state, the playlist
 * (ordered list of ringtones for prev/next), and whether the full-screen
 * player is open. The actual <audio> element lives here so it persists
 * across tab changes.
 */

import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
} from 'react';
import type { WallpapersResponse } from '@/lib/collections/wallpapers';

// Same media-type check used elsewhere (e.g. WallpaperViewer.tsx's isRingtone).
// The playlist handed to play()/togglePlayPause() can be a mixed grid (wallpapers +
// live wallpapers + ringtones) coming from Library/Favorites/Home. prev/next and
// auto-advance must only ever walk the ringtone subset of that list — never select
// or surface a non-audio item as "currentRingtone".
function isRingtone(item: WallpapersResponse): boolean {
  const stored = (item as WallpapersResponse & { mediaType?: string }).mediaType;
  return stored === 'ringtone';
}

function getRingtoneOnly(playlist: WallpapersResponse[]): WallpapersResponse[] {
  return playlist.filter(isRingtone);
}

export interface RingtonePlayerState {
  /** Currently loaded ringtone, or null when nothing is queued */
  currentRingtone: WallpapersResponse | null;
  /** Ordered playlist for prev/next navigation */
  playlist: WallpapersResponse[];
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  /** Whether the full-screen player overlay is visible */
  fullScreenOpen: boolean;
  /** Whether the floating mini player should be shown. Only becomes true after Full View has been opened at least once for the current ringtone session. */
  floatingPlayerEnabled: boolean;
  isFavorited: boolean;
  isPurchased: boolean;
}

export interface RingtonePlayerActions {
  /** Load a ringtone and start playing, setting a playlist for prev/next */
  play: (ringtone: WallpapersResponse, playlist?: WallpapersResponse[]) => void;
  pause: () => void;
  resume: () => void;
  /** Stop playback and dismiss the player (clears currentRingtone) */
  stop: () => void;
  togglePlayPause: (ringtone: WallpapersResponse, playlist?: WallpapersResponse[]) => void;
  seekTo: (time: number) => void;
  playNext: () => void;
  playPrev: () => void;
  openFullScreen: () => void;
  closeFullScreen: () => void;
  /** Pause audio and hide the floating mini player (used when an overlay/window opens) */
  pauseAndHideFloating: () => void;
  /** Show the floating mini player again (without resuming audio) when overlays close */
  showFloating: () => void;
  /** Pause audio and show the floating mini player. Used when leaving the regular mini player context (e.g. nav-bar tab switch) so the user keeps a paused indicator. */
  pauseAndShowFloating: () => void;
  setFavorited: (v: boolean) => void;
  setPurchased: (v: boolean) => void;
}

const defaultState: RingtonePlayerState = {
  currentRingtone: null,
  playlist: [],
  isPlaying: false,
  currentTime: 0,
  duration: 0,
  fullScreenOpen: false,
  floatingPlayerEnabled: false,
  isFavorited: false,
  isPurchased: false,
};

const PlayerStateCtx = createContext<RingtonePlayerState>(defaultState);
const PlayerActionsCtx = createContext<RingtonePlayerActions>({
  play: () => {},
  pause: () => {},
  resume: () => {},
  stop: () => {},
  togglePlayPause: () => {},
  seekTo: () => {},
  playNext: () => {},
  playPrev: () => {},
  openFullScreen: () => {},
  closeFullScreen: () => {},
  pauseAndHideFloating: () => {},
  showFloating: () => {},
  pauseAndShowFloating: () => {},
  setFavorited: () => {},
  setPurchased: () => {},
});

export function RingtonePlayerProvider({ children }: { children: React.ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [state, setState] = useState<RingtonePlayerState>(defaultState);

  // Keep a ref to the current state to avoid stale closures in audio event handlers
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  });

  // Tracks whether floatingPlayerEnabled was true before pauseAndHideFloating() was
  // called. showFloating() only restores it when this is true — so mini-player does
  // not pop up unexpectedly if it was never enabled in the first place.
  const floatingWasEnabledRef = useRef(false);

  // Media Session API — update notification panel metadata whenever track or play state changes
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;

    const ringtone = state.currentRingtone;

    if (!ringtone) {
      navigator.mediaSession.metadata = null;
      navigator.mediaSession.playbackState = 'none';
      return;
    }

    // Build artwork array: use thumbnailUrl or imageUrl if it looks like an image (not audio)
    const artworkUrl = ringtone.thumbnailUrl ?? (
      ringtone.imageUrl && /\.(jpe?g|png|webp|gif|svg)(\?|$)/i.test(ringtone.imageUrl)
        ? ringtone.imageUrl
        : undefined
    );
    const artwork: MediaImage[] = artworkUrl
      ? [{ src: artworkUrl, sizes: '512x512', type: 'image/jpeg' }]
      : [];

    navigator.mediaSession.metadata = new MediaMetadata({
      title: ringtone.title ?? 'Untitled',
      artist: '',
      album: '',
      artwork,
    });

    navigator.mediaSession.playbackState = state.isPlaying ? 'playing' : 'paused';
  }, [state.currentRingtone, state.isPlaying]);

  // Refs so Media Session action handlers can always call latest versions
  const pauseRef = useRef<() => void>(() => {});
  const resumeRef = useRef<() => void>(() => {});
  const playNextRef = useRef<() => void>(() => {});
  const playPrevRef = useRef<() => void>(() => {});

  // Initialise the shared <audio> element once
  useEffect(() => {
    const audio = new Audio();
    audio.preload = 'metadata';
    audioRef.current = audio;

    // Wire up Media Session action handlers if supported
    if ('mediaSession' in navigator) {
      navigator.mediaSession.setActionHandler('play', () => resumeRef.current?.());
      navigator.mediaSession.setActionHandler('pause', () => pauseRef.current?.());
      navigator.mediaSession.setActionHandler('nexttrack', () => playNextRef.current?.());
      navigator.mediaSession.setActionHandler('previoustrack', () => playPrevRef.current?.());
    }

    const onTimeUpdate = () => {
      setState(prev => ({
        ...prev,
        currentTime: audio.currentTime,
        duration: isFinite(audio.duration) ? audio.duration : prev.duration,
      }));
    };

    const onLoadedMetadata = () => {
      setState(prev => ({
        ...prev,
        duration: isFinite(audio.duration) ? audio.duration : 0,
      }));
    };

    const onEnded = () => {
      // Auto-advance to next track — only ever among ringtone items in the playlist.
      setState(prev => {
        const { playlist, currentRingtone } = prev;
        if (!currentRingtone || playlist.length === 0) {
          return { ...prev, isPlaying: false, currentTime: 0 };
        }
        const ringtones = getRingtoneOnly(playlist);
        const idx = ringtones.findIndex(r => r.id === currentRingtone.id);
        const nextIdx = idx >= 0 && idx < ringtones.length - 1 ? idx + 1 : -1;
        if (nextIdx === -1) {
          return { ...prev, isPlaying: false, currentTime: 0 };
        }
        const next = ringtones[nextIdx];
        audio.src = next.imageUrl ?? '';
        audio.currentTime = 0;
        audio.play().catch(() => {});
        return { ...prev, currentRingtone: next, currentTime: 0, duration: 0, isPlaying: true };
      });
    };

    const onPlay = () => setState(prev => ({ ...prev, isPlaying: true }));
    const onPause = () => setState(prev => ({ ...prev, isPlaying: false }));

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);

    return () => {
      audio.pause();
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
    };
  }, []);

  const play = useCallback((ringtone: WallpapersResponse, playlist?: WallpapersResponse[]) => {
    const audio = audioRef.current;
    if (!audio) return;
    const list = playlist ?? stateRef.current.playlist;
    audio.src = ringtone.imageUrl ?? '';
    audio.currentTime = 0;
    audio.play().catch(() => {});
    setState(prev => ({
      ...prev,
      currentRingtone: ringtone,
      playlist: list,
      isPlaying: true,
      currentTime: 0,
      duration: 0,
    }));
  }, []);

  const pause = useCallback(() => {
    audioRef.current?.pause();
    setState(prev => ({ ...prev, isPlaying: false }));
  }, []);

  const resume = useCallback(() => {
    audioRef.current?.play().catch(() => {});
    setState(prev => ({ ...prev, isPlaying: true }));
  }, []);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    }
    floatingWasEnabledRef.current = false;
    setState(prev => ({
      ...prev,
      currentRingtone: null,
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      fullScreenOpen: false,
      floatingPlayerEnabled: false,
    }));
  }, []);

  const togglePlayPause = useCallback((ringtone: WallpapersResponse, playlist?: WallpapersResponse[]) => {
    const audio = audioRef.current;
    if (!audio) return;
    const current = stateRef.current;

    if (current.currentRingtone?.id === ringtone.id) {
      // Same track — just toggle
      if (current.isPlaying) {
        audio.pause();
      } else {
        audio.play().catch(() => {});
      }
    } else {
      // Different track — load and play
      const list = playlist ?? current.playlist;
      audio.src = ringtone.imageUrl ?? '';
      audio.currentTime = 0;
      audio.play().catch(() => {});
      setState(prev => ({
        ...prev,
        currentRingtone: ringtone,
        playlist: list,
        isPlaying: true,
        currentTime: 0,
        duration: 0,
      }));
    }
  }, []);

  const seekTo = useCallback((time: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = time;
    setState(prev => ({ ...prev, currentTime: time }));
  }, []);

  const playNext = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const { playlist, currentRingtone } = stateRef.current;
    if (!currentRingtone) return;
    const ringtones = getRingtoneOnly(playlist);
    // No other ringtone to advance to (e.g. only one ringtone in the library) — no-op.
    if (ringtones.length <= 1) return;
    const idx = ringtones.findIndex(r => r.id === currentRingtone.id);
    if (idx === -1) return;
    const nextIdx = (idx + 1) % ringtones.length;
    const next = ringtones[nextIdx];
    audio.src = next.imageUrl ?? '';
    audio.currentTime = 0;
    audio.play().catch(() => {});
    setState(prev => ({
      ...prev,
      currentRingtone: next,
      currentTime: 0,
      duration: 0,
      isPlaying: true,
      isPurchased: false,
    }));
  }, []);

  const playPrev = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const { playlist, currentRingtone, currentTime } = stateRef.current;
    if (!currentRingtone) return;
    // If > 3s into track, restart instead of going to previous
    if (currentTime > 3) {
      audio.currentTime = 0;
      setState(prev => ({ ...prev, currentTime: 0 }));
      return;
    }
    const ringtones = getRingtoneOnly(playlist);
    // No other ringtone to go back to (e.g. only one ringtone in the library) — no-op.
    if (ringtones.length <= 1) return;
    const idx = ringtones.findIndex(r => r.id === currentRingtone.id);
    if (idx === -1) return;
    const prevIdx = idx > 0 ? idx - 1 : ringtones.length - 1;
    const prevItem = ringtones[prevIdx];
    audio.src = prevItem.imageUrl ?? '';
    audio.currentTime = 0;
    audio.play().catch(() => {});
    setState(prevState => ({
      ...prevState,
      currentRingtone: prevItem,
      currentTime: 0,
      duration: 0,
      isPlaying: true,
      isPurchased: false,
    }));
  }, []);

  const openFullScreen = useCallback(() => {
    setState(prev => ({ ...prev, fullScreenOpen: true, floatingPlayerEnabled: true }));
  }, []);

  const closeFullScreen = useCallback(() => {
    // When closing the full-screen player, enable the floating mini-player so
    // it remains visible on whatever page the user navigates to (e.g. creator
    // profile page reached via the creator label tap).
    setState(prev => ({
      ...prev,
      fullScreenOpen: false,
      floatingPlayerEnabled: prev.currentRingtone ? true : prev.floatingPlayerEnabled,
    }));
  }, []);

  const pauseAndHideFloating = useCallback(() => {
    // If there's a current ringtone (regardless of whether playback started from
    // an inline mini player or full-screen view), we want showFloating() to bring
    // the floating bar back once the overlay closes.
    floatingWasEnabledRef.current = !!stateRef.current.currentRingtone;
    audioRef.current?.pause();
    setState(prev => ({ ...prev, isPlaying: false, floatingPlayerEnabled: false }));
  }, []);

  const showFloating = useCallback(() => {
    // Only restore the mini player if it was actually visible before the hide.
    // This prevents it from appearing when the user never opened the full-screen player.
    if (!floatingWasEnabledRef.current) return;
    setState(prev => (
      prev.currentRingtone ? { ...prev, floatingPlayerEnabled: true } : prev
    ));
  }, []);

  const pauseAndShowFloating = useCallback(() => {
    // Pause audio and force the floating mini player visible so it persists
    // across tab navigation while the regular in-card mini player is no longer
    // on screen. Marking the ref ensures showFloating() will continue to
    // restore the bar after subsequent overlay open/close cycles.
    if (!stateRef.current.currentRingtone) return;
    floatingWasEnabledRef.current = true;
    audioRef.current?.pause();
    setState(prev => (
      prev.currentRingtone
        ? { ...prev, isPlaying: false, floatingPlayerEnabled: true }
        : prev
    ));
  }, []);

  const setFavorited = useCallback((v: boolean) => {
    setState(prev => ({ ...prev, isFavorited: v }));
  }, []);

  const setPurchased = useCallback((v: boolean) => {
    setState(prev => ({ ...prev, isPurchased: v }));
  }, []);

  // Keep action refs current so Media Session handlers never call stale closures
  useLayoutEffect(() => {
    pauseRef.current = pause;
    resumeRef.current = resume;
    playNextRef.current = playNext;
    playPrevRef.current = playPrev;
  });

  const actions: RingtonePlayerActions = {
    play,
    pause,
    resume,
    stop,
    togglePlayPause,
    seekTo,
    playNext,
    playPrev,
    openFullScreen,
    closeFullScreen,
    pauseAndHideFloating,
    showFloating,
    pauseAndShowFloating,
    setFavorited,
    setPurchased,
  };

  return (
    <PlayerStateCtx.Provider value={state}>
      <PlayerActionsCtx.Provider value={actions}>
        {children}
      </PlayerActionsCtx.Provider>
    </PlayerStateCtx.Provider>
  );
}

export function useRingtonePlayer(): RingtonePlayerState & RingtonePlayerActions {
  const state = useContext(PlayerStateCtx);
  const actions = useContext(PlayerActionsCtx);
  return { ...state, ...actions };
}

export function useRingtonePlayerState(): RingtonePlayerState {
  return useContext(PlayerStateCtx);
}

export function useRingtonePlayerActions(): RingtonePlayerActions {
  return useContext(PlayerActionsCtx);
}
